// HM-312.r cluster 15 visual rework: tile camera matched a
// docs/design-references/dashboard-v2-reference.html (.room-tile).
//   - aspect-ratio 1.4:1
//   - padding 12 11, borderRadius 10
//   - border 1.5px solid colore stato (inline style: tailwind no border-[1.5px])
//   - bg tinted per stato (#F4FAF1 LIB, #FCF1EA OCC, ...)
//   - room-num 19/700 letter-spacing -0.01em
//   - icon 15
//   - room-status 10/600 uppercase letter-spacing 0.3
//   - room-meta 9/500 muted (countdown / temp / arrivo)
//   - hover: translateY(-2px) + shadow forte
//   - selected: ring arancio 2px + shadow forte arancio

import { HM_ICON_STROKE_WIDTH } from '@hotel-manager/ui/icons';
import { cn } from '@hotel-manager/ui/lib/cn';
import type { BookingDto, RoomDb } from '../../api/rooms.js';
import { isUnpaid, unpaidAmount } from '../../lib/paymentStatus.js';
import { UnpaidBadge } from '../UnpaidBadge.js';
import { formatRelative, formatTime, mapRoomStatus } from './lib.js';

interface Props {
  room: RoomDb;
  bookings: BookingDto[];
  now: Date;
  selected: boolean;
  onSelect: (room: RoomDb) => void;
  /**
   * HM-473 audit A7: lampeggio attivo (Spec §7.4) quando il cliente ha
   * usato il codice alla sbarra uscita. L'operatore deve confermare
   * check-out o "ospite ancora in camera".
   */
  blinkingExit?: boolean;
}

export function RoomTile({ room, bookings, now, selected, onSelect, blinkingExit }: Props) {
  const status = mapRoomStatus(room.status);
  const StatusIcon = status.icon;

  const activeCheckedIn = bookings.find((b) => b.roomId === room.id && b.status === 'checked_in');
  const upcomingReserved = bookings
    .filter((b) => b.roomId === room.id && b.status === 'confirmed')
    .filter((b) => new Date(b.startAt).getTime() > now.getTime())
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())[0];

  const { statusLabel, metaLabel } = computeLabels({
    room,
    activeCheckedIn,
    upcomingReserved,
    now,
    fallbackStatus: status.label,
  });

  return (
    <button
      type="button"
      data-testid={`room-tile-${room.code}`}
      data-status={room.status}
      data-selected={selected}
      onClick={() => onSelect(room)}
      aria-label={`Camera ${room.code} ${status.label}`}
      aria-pressed={selected}
      style={{
        // Tailwind nativo non ha border-[1.5px]: serve inline (mockup specifica 1.5)
        borderWidth: '1.5px',
        borderStyle: 'solid',
        borderColor: status.borderHex,
      }}
      className={cn(
        'rounded-[10px] cursor-pointer text-left flex flex-col justify-between',
        // Adattamento tablet 2026-07-22: con le colonne fluide la tile e' piu'
        // larga, padding e tipografia crescono per leggersi a un braccio di
        // distanza (plancia operativa, non lista desktop).
        'aspect-[1.4/1] px-3.5 py-3.5 max-xl:px-2.5 max-xl:py-2.5',
        'transition-all duration-150 ease-out',
        'hover:-translate-y-0.5 hover:shadow-hm-md',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary',
        status.tileBgClass,
        selected && 'ring-2 ring-accent-primary shadow-[0_6px_16px_rgba(233,123,61,0.25)]',
        // HM-473 audit A7: blink Spec §7.4 sbarra uscita (1Hz ring acceso/spento).
        blinkingExit && 'blink-exit',
      )}
    >
      {/* w-full esplicito: dentro <button> i figli flex non si stretchano in
          modo affidabile (quirk UA) — con tile larghe l'icona restava
          incollata al numero invece che nell'angolo destro. */}
      <div className="flex justify-between items-start w-full">
        <span className="text-[23px] max-xl:text-[20px] font-bold text-text-primary leading-none tracking-[-0.01em]">
          {room.code}
        </span>
        <StatusIcon size={17} strokeWidth={HM_ICON_STROKE_WIDTH} className={status.classText} />
      </div>
      {/* Revisione pagamenti 2026-07-22: pill piccola "DA INCASSARE €X" su
          camera occupata con ospite checked_in non (interamente) pagato. */}
      {activeCheckedIn && isUnpaid(activeCheckedIn) && (
        <UnpaidBadge amount={unpaidAmount(activeCheckedIn)} size="sm" className="self-start" />
      )}
      <div className="w-full">
        <div
          className={cn(
            'text-[11.5px] font-semibold uppercase tracking-[0.3px] truncate',
            status.tileStatusClass,
          )}
        >
          {statusLabel}
        </div>
        <div className="text-[10.5px] font-medium text-text-muted mt-0.5 truncate">{metaLabel}</div>
      </div>
    </button>
  );
}

function computeLabels(args: {
  room: RoomDb;
  activeCheckedIn: BookingDto | undefined;
  upcomingReserved: BookingDto | undefined;
  now: Date;
  fallbackStatus: string;
}): { statusLabel: string; metaLabel: string } {
  const { room, activeCheckedIn, upcomingReserved, now, fallbackStatus } = args;

  if (room.status === 'occupied' && activeCheckedIn) {
    const guest = compactGuestName(activeCheckedIn.guestName);
    // HM-531 fix camere: mostra mode (DAY/NOTTE) nel meta per distinguere
    // a colpo d'occhio day_use da pernottamento.
    const modeTag = activeCheckedIn.service === 'day_use' ? 'DAY' : 'NOTTE';
    // HM-453 fix overstay: se end_at e' nel passato di > 1 giorno mostriamo
    // un'etichetta dedicata "OVERSTAY Ng" al posto del countdown tradizionale.
    const endMs = new Date(activeCheckedIn.endAt).getTime();
    const overstayMs = now.getTime() - endMs;
    const overstayDays = Math.floor(overstayMs / 86_400_000);
    if (overstayDays >= 1) {
      return { statusLabel: guest, metaLabel: `${modeTag} · OVERSTAY ${overstayDays}g` };
    }
    return {
      statusLabel: guest,
      metaLabel: `${modeTag} · ↓ ${formatRelative(new Date(activeCheckedIn.endAt), now)}`,
    };
  }

  if (room.status === 'reserved' && upcomingReserved) {
    const guest = compactGuestName(upcomingReserved.guestName);
    const modeTag = upcomingReserved.service === 'day_use' ? 'DAY' : 'NOTTE';
    return {
      statusLabel: guest,
      metaLabel: `${modeTag} · arrivo ${formatTime(new Date(upcomingReserved.startAt))}`,
    };
  }

  if (room.status === 'free') {
    return { statusLabel: 'Libera', metaLabel: '21°C' };
  }
  if (room.status === 'dirty') {
    return { statusLabel: 'Da pulire', metaLabel: 'cleanup pending' };
  }
  if (room.status === 'out_of_order') {
    return { statusLabel: 'Guasta', metaLabel: 'segnalata' };
  }

  return { statusLabel: fallbackStatus, metaLabel: '' };
}

/** "Mario Rossi" -> "M. Rossi" come da mockup. */
function compactGuestName(name: string | null | undefined): string {
  if (!name) return 'Ospite';
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return parts[0] ?? name;
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return `${first}. ${last}`;
}
